<!-- Ephemeris: toolbar (periode, bladeren, nu, weergave, kolommen, afdrukken, export) + één tabel
     voor beide weergaven. Tabel, afdruk en export komen uit dezelfde weergavelijst (display()). -->
<header class="ephemeris__print-header">
  <span class="ephemeris__print-title">{{ t('title') }}</span>
  <span class="ephemeris__print-options">{{ optionsSummary() }}</span>
</header>

<div class="ephemeris__toolbar">
  <div class="ephemeris__period">
    <button
      eBtn
      size="sm"
      squared
      type="button"
      color="highlight"
      icon="chevron-left"
      [disabled]="!canGoBack() || loading()"
      [tooltip]="t('previousPeriod')"
      [attr.aria-label]="t('previousPeriod')"
      (click)="shift(-1)"
    ></button>

    <div class="ephemeris__popover-host" #periodHost>
      <button
        eBtn
        size="sm"
        type="button"
        color="highlight"
        icon="calendar-days"
        class="ephemeris__period-btn"
        [tooltip]="t('choosePeriod')"
        [attr.aria-expanded]="periodOpen()"
        (click)="togglePeriod()"
      >
        <span class="ephemeris__period-label">{{ periodLabel() }}</span>
      </button>
      @if (periodOpen()) {
        <div class="ephemeris__popover ephemeris__popover--period" role="dialog" [attr.aria-label]="t('choosePeriod')">
          <p class="ephemeris__popover-hint">{{ t('choosePeriodHint', { days: bounds().maxWindowDays }) }}</p>
          <e-date-picker
            range
            showValue
            [locale]="lang()"
            [minYear]="minYear()"
            [maxYear]="maxYear()"
            [value]="pickerValue()"
            (valueChange)="onRangePicked($event)"
          ></e-date-picker>
        </div>
      }
    </div>

    <button
      eBtn
      size="sm"
      squared
      type="button"
      color="highlight"
      icon="chevron-right"
      [disabled]="!canGoForward() || loading()"
      [tooltip]="t('nextPeriod')"
      [attr.aria-label]="t('nextPeriod')"
      (click)="shift(1)"
    ></button>

    <button eBtn size="sm" type="button" color="highlight" icon="clock" (click)="goToNow()">
      {{ t('now') }}
    </button>
  </div>

  <div class="ephemeris__options">
    <!-- Altijd aanwezig (enkel zichtbaar tijdens het laden), zodat de toggles niet verspringen. -->
    <span
      class="ephemeris__loading"
      role="status"
      [class.is-visible]="loading()"
      [attr.aria-hidden]="!loading()"
      [attr.title]="loading() ? t('loading') : null"
    >
      <i class="fas fa-spinner" [class.fa-spin]="loading()" aria-hidden="true"></i>
    </span>
    <!-- Zelfde segmented toggle als de infopanelen, cheat sheets en transits (actief = primary). -->
    <e-segmented-toggle
      testId="ephemeris-mode"
      [groupLabel]="t('mode')"
      [options]="modeOptions()"
      [value]="mode()"
      (valueChange)="onModeChange($event)"
    ></e-segmented-toggle>

    <!-- Altijd aanwezig; bij de wissels uitgeschakeld (notatie geldt enkel voor dagposities). -->
    <e-segmented-toggle
      testId="ephemeris-notation"
      [disabled]="mode() !== 'daily'"
      [groupLabel]="mode() === 'daily' ? t('notation') : t('notationOnlyDaily')"
      [options]="notationOptions()"
      [value]="notation()"
      (valueChange)="onNotationChange($event)"
    ></e-segmented-toggle>

    <e-segmented-toggle
      testId="ephemeris-zone"
      [groupLabel]="t('timezone')"
      [options]="zoneOptions()"
      [value]="useUtc() ? 'utc' : 'local'"
      (valueChange)="onZoneChange($event)"
    ></e-segmented-toggle>

    <div class="ephemeris__popover-host" #columnsHost>
      <button
        eBtn
        size="sm"
        type="button"
        color="highlight"
        icon="table-columns"
        [attr.aria-expanded]="columnsOpen()"
        (click)="toggleColumns()"
      >
        {{ t('columns') }}
      </button>
      @if (columnsOpen()) {
        <div class="ephemeris__popover ephemeris__popover--columns" role="dialog" [attr.aria-label]="t('columns')">
          <ul class="ephemeris__column-list">
            @for (option of columnOptions(); track option.key) {
              <li>
                <label class="ephemeris__column-option" [class.is-hidden]="!option.visible">
                  <input type="checkbox" [checked]="option.visible" (change)="toggleBody(option.key)" />
                  <span class="ephemeris__glyph" [style.color]="option.color" aria-hidden="true">{{ option.glyph }}</span>
                  <span>{{ option.name }}</span>
                </label>
              </li>
            }
          </ul>
          <button eBtn size="xs" type="button" color="highlight" icon="check" (click)="showAllBodies()">
            {{ t('showAll') }}
          </button>
        </div>
      }
    </div>

    <button
      eBtn
      size="sm"
      type="button"
      color="highlight"
      icon="print"
      data-testid="ephemeris-print"
      [disabled]="!display()"
      [tooltip]="t('printHint')"
      (click)="print()"
    >
      {{ t('print') }}
    </button>

    <div class="ephemeris__popover-host" #exportHost>
      <button
        eBtn
        size="sm"
        type="button"
        color="highlight"
        icon="file-export"
        data-testid="ephemeris-export"
        [disabled]="!display()"
        [loading]="exporting()"
        [attr.aria-expanded]="exportOpen()"
        (click)="toggleExport()"
      >
        {{ t('export') }}
      </button>
      @if (exportOpen()) {
        <div class="ephemeris__popover ephemeris__popover--export" role="menu" [attr.aria-label]="t('export')">
          <p class="ephemeris__popover-hint">{{ t('exportHint') }}</p>
          <button type="button" class="ephemeris__menu-item" role="menuitem" data-testid="ephemeris-export-excel" (click)="exportExcel()">
            <i class="fas fa-file-excel" aria-hidden="true"></i>
            <span class="ephemeris__menu-text">
              <span class="ephemeris__menu-title">{{ t('exportExcel') }}</span>
              <span class="ephemeris__menu-sub">{{ t('exportExcelHint') }}</span>
            </span>
          </button>
          <button type="button" class="ephemeris__menu-item" role="menuitem" data-testid="ephemeris-export-csv" (click)="exportCsv()">
            <i class="fas fa-file-csv" aria-hidden="true"></i>
            <span class="ephemeris__menu-text">
              <span class="ephemeris__menu-title">{{ t('exportCsv') }}</span>
              <span class="ephemeris__menu-sub">{{ t('exportCsvHint') }}</span>
            </span>
          </button>
        </div>
      }
    </div>
  </div>
</div>

@if (rangeClamped()) {
  <p class="ephemeris__notice">{{ t('rangeClamped', { days: bounds().maxWindowDays }) }}</p>
}
@if (error(); as loadError) {
  <div class="ephemeris__notice ephemeris__notice--error" role="alert">
    <span>{{ loadError === 'invalid' && errorMessage() ? errorMessage() : t('errors.' + loadError) }}</span>
    @if (loadError !== 'invalid') {
      <button eBtn size="xs" type="button" color="highlight" icon="rotate-right" (click)="retry()">
        {{ t('retry') }}
      </button>
    }
  </div>
}

<div class="ephemeris__scroll" #scrollContainer [class.is-loading]="loading()">
  @if (display(); as view) {
    @let cols = columns();
    @let colSelected = columnSelected();
    @let cellSelected = selectedCell();
    @let pinned = pinnedRowId();
    @let draft = insertDraft();
    @let daily = view.mode === 'daily';
    <table class="ephemeris-table" [class.ephemeris-table--daily]="daily">
      <thead>
        <tr>
          <th class="ephemeris-table__time" scope="col">{{ daily ? t('dateTime') : t('moment') }}</th>
          @for (column of cols; track column.colKey) {
            <th
              class="ephemeris-table__body"
              scope="col"
              tabindex="0"
              [class.is-col-selected]="colSelected[$index]"
              [attr.aria-pressed]="colSelected[$index]"
              [attr.title]="column.title"
              (click)="toggleColumn(column.colKey)"
              (keydown.enter)="toggleColumn(column.colKey)"
              (keydown.space)="$event.preventDefault(); toggleColumn(column.colKey)"
            >
              <span class="ephemeris__glyph" [style.color]="column.color" aria-hidden="true">{{ column.glyph }}</span>
              <span class="ephemeris-table__body-name">
                {{ column.name }}@if (column.moonLater) { <span class="ephemeris-table__moon-time">{{ t('moonLater') }}</span>}
              </span>
            </th>
          }
        </tr>
      </thead>
      <tbody>
        @for (row of view.rows; track row.id; let rowIndex = $index; let lastRow = $last) {
          @if (row.groupLabel) {
            <tr class="ephemeris-table__day">
              <th [attr.colspan]="cols.length + 1" scope="rowgroup">{{ row.groupLabel }}</th>
            </tr>
          }
          <tr
            class="ephemeris-table__row"
            [class.ephemeris-table__row--initial]="row.kind === 'initial'"
            [class.is-inserted]="row.kind === 'inserted'"
            [class.is-current]="rowIndex === view.currentIndex"
            [class.is-pinned]="row.id === pinned"
            [class.is-day-start]="!!row.dateLabel"
            [class.is-last]="lastRow"
          >
            <th
              class="ephemeris-table__time"
              scope="row"
              [attr.title]="row.kind === 'inserted' ? t('insertedRowHint') : t('pinRowHint')"
              (click)="togglePinnedRow(row.id)"
            >
              <span class="ephemeris-table__time-inner">
                @if (daily) {
                  <span class="ephemeris-table__date">{{ row.dateLabel ?? '' }}</span>
                }
                <span class="ephemeris-table__clock" [class.is-muted]="daily || !!row.prefix">
                  @if (row.prefix) {<span class="ephemeris-table__prefix">{{ row.prefix }}</span> }{{ row.time }}<span class="ephemeris-table__seconds">{{ row.seconds ?? '' }}</span>
                </span>
                @if (ichingIconsVisible() && row.iching) {
                  <!-- Classic I Ching: hexagram van de dag (zon, maan, dagteller) van deze rij. -->
                  <button
                    type="button"
                    class="ephemeris-table__iching"
                    data-testid="ephemeris-iching"
                    [attr.title]="t('ichingDay')"
                    [attr.aria-label]="t('ichingDay')"
                    (click)="$event.stopPropagation(); openIChingDay(row, $event)"
                  >
                    <i class="fa-regular fa-scroll-old" aria-hidden="true"></i>
                  </button>
                }
                @if (row.kind === 'inserted') {
                  <button
                    type="button"
                    class="ephemeris-table__remove"
                    [attr.title]="t('removeInserted')"
                    [attr.aria-label]="t('removeInserted')"
                    (click)="$event.stopPropagation(); removeInserted(row)"
                  >
                    <i class="fas fa-xmark" aria-hidden="true"></i>
                  </button>
                }
              </span>
              <!-- Tussen deze rij en de volgende (of het einde van de periode) een eigen tijdstip invoegen. -->
              <button
                type="button"
                class="ephemeris-table__insert-btn"
                data-testid="ephemeris-insert"
                [attr.title]="t('insertRowHint')"
                [attr.aria-label]="t('insertRowHint')"
                (click)="$event.stopPropagation(); openInsert(rowIndex)"
              >
                <i class="fas fa-plus" aria-hidden="true"></i>
              </button>
            </th>
            @for (cell of row.cells; track $index) {
              <td
                class="ephemeris-table__cell"
                [class.is-changed]="cell.changed"
                [class.is-station]="cell.station !== null"
                [class.is-col-selected]="colSelected[$index]"
                [class.is-cell-selected]="cellSelected !== null && cellSelected.rowId === row.id && cellSelected.colKey === cols[$index].colKey"
                [attr.title]="cell.tooltip"
                (click)="toggleCell(row.id, cols[$index].colKey)"
              >
                {{ cell.text }}@if (cell.signGlyph) {<span class="ephemeris-table__sign" aria-hidden="true">{{ cell.signGlyph }}</span>}@if (cell.station === 'retrograde') {<span class="ephemeris-table__retro">S℞</span>} @else if (cell.station === 'direct') {<span class="ephemeris-table__retro">SD</span>} @else if (cell.retrograde) {<span class="ephemeris-table__retro" [attr.title]="retrogradeLabel()">℞</span>}
              </td>
            }
          </tr>
          @if (draft && draft.afterId === row.id) {
            <tr class="ephemeris-table__insert">
              <td [attr.colspan]="cols.length + 1">
                <!-- Enkel de tijd is te kiezen; de dag volgt uit het venster (een dagkeuze enkel bij een venster van een dag of langer). -->
                @let candidate = insertCandidate();
                @let bounds = insertTimeBounds(draft);
                <div class="ephemeris-table__insert-form">
                  <label class="ephemeris-table__insert-label">
                    <span>{{ insertRangeLabel(draft) }}</span>
                    @if (draft.needsDay) {
                      <ng-select
                        class="ephemeris-table__insert-day"
                        data-testid="ephemeris-insert-day"
                        bindValue="value"
                        bindLabel="label"
                        [items]="insertDayOptions()"
                        [clearable]="false"
                        [searchable]="false"
                        [ngModel]="draft.day"
                        (ngModelChange)="onInsertDay($event)"
                      ></ng-select>
                    } @else {
                      <span class="ephemeris-table__insert-date" data-testid="ephemeris-insert-date">{{ insertDateLabel() }}</span>
                    }
                    <input
                      #insertInput
                      type="time"
                      step="1"
                      data-testid="ephemeris-insert-input"
                      [attr.min]="bounds.min"
                      [attr.max]="bounds.max"
                      [value]="draft.time"
                      [attr.aria-invalid]="!candidate"
                      (input)="onInsertTime(insertInput.value)"
                      (keydown.enter)="confirmInsert()"
                    />
                  </label>
                  <button
                    eBtn
                    size="xs"
                    type="button"
                    color="primary"
                    icon="plus"
                    data-testid="ephemeris-insert-confirm"
                    [disabled]="!candidate"
                    (click)="confirmInsert()"
                  >
                    {{ t('insertConfirm') }}
                  </button>
                  <button eBtn size="xs" type="button" color="highlight" (click)="cancelInsert()">
                    {{ t('insertCancel') }}
                  </button>
                  @if (!candidate) {
                    <span class="ephemeris-table__insert-error" role="alert">{{ t('insertInvalid') }}</span>
                  }
                </div>
              </td>
            </tr>
          }
          @if (rowIndex === view.currentIndex) {
            <tr class="ephemeris-table__now" data-now-anchor>
              <td [attr.colspan]="cols.length + 1">
                <span class="ephemeris-table__now-label">{{ t('nowMarker') }} · {{ nowLabel() }}</span>
              </td>
            </tr>
          }
        }
      </tbody>
    </table>
    @if (view.rows.length === 0 || (!daily && view.rows.length === 1)) {
      <p class="ephemeris__empty">{{ daily ? t('noDays') : t('noRows') }}</p>
    }
  } @else if (loading()) {
    <p class="ephemeris__empty">{{ t('loading') }}</p>
  }
</div>

<footer class="ephemeris__footer">
  @if (mode() === 'daily') {
    <span>{{ t('legendDaily') }}</span>
    <span>{{ t('dailyTimes', { zone: zoneLabel() }) }} · {{ coverageLabel() }}</span>
  } @else {
    <span>{{ t('legend') }}</span>
    <span>{{ t('timesIn', { zone: zoneLabel() }) }} · {{ coverageLabel() }}</span>
  }
  <span class="ephemeris__footer-hint">{{ t('interactionHint') }}</span>
</footer>
